import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { generateCertificate } from '../services/exportService';
import Footer from '../components/Footer';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'https://templimizeai-coursecreatorvault.onrender.com/api';

const CertificateGenerator: React.FC = () => {
  const { courseId } = useParams<{ courseId: string }>();
  const { token, user } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();

  const [course, setCourse] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [studentName, setStudentName] = useState('');
  const [instructorName, setInstructorName] = useState('');
  const [completionDate, setCompletionDate] = useState(new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  }));
  const [brandName, setBrandName] = useState('');

  useEffect(() => {
    fetchCourse();
    fetchBrandSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseId]);

  const fetchCourse = async () => {
    try {
      const response = await axios.get(`${API_URL}/courses`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const foundCourse = response.data.courses.find((c: any) => c.id === courseId);
      if (foundCourse) {
        setCourse(foundCourse);
      } else {
        toast.error('Course not found');
        navigate('/dashboard');
      }
    } catch (error) {
      console.error('Error fetching course:', error);
      toast.error('Failed to load course');
    } finally {
      setLoading(false);
    }
  };

  const fetchBrandSettings = async () => {
    try {
      const response = await axios.get(`${API_URL}/brand-settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data) {
        setBrandName(response.data.company_name || 'Templimize AI');
      }
    } catch (error) {
      console.log('No brand settings found, using defaults');
    }
  };

  const handleGenerate = () => {
    if (!studentName.trim()) {
      toast.error('Please enter a student name');
      return;
    }

    generateCertificate({
      studentName: studentName.trim(),
      courseName: course.title,
      completionDate,
      instructorName: instructorName.trim() || user?.name || 'Course Instructor',
      brandName: brandName || 'Templimize AI'
    });

    toast.success('Certificate generated successfully!');
  };

  const handleBulkGenerate = () => {
    const names = studentName.split('\n').filter(n => n.trim());
    if (names.length === 0) {
      toast.error('Please enter at least one student name');
      return;
    }

    names.forEach((name, index) => {
      setTimeout(() => {
        generateCertificate({
          studentName: name.trim(),
          courseName: course.title,
          completionDate,
          instructorName: instructorName.trim() || user?.name || 'Course Instructor',
          brandName: brandName || 'Templimize AI'
        });
      }, index * 500); // Stagger downloads to prevent browser blocking
    });

    toast.success(`Generating ${names.length} certificates...`);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block w-12 h-12 border-4 border-purple-600 border-t-transparent rounded-full animate-spin mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate(`/course/${courseId}`)}
                className="text-gray-600 hover:text-gray-900"
              >
                ← Back to Course
              </button>
              <div>
                <h1 className="text-2xl font-bold bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 bg-clip-text text-transparent">
                  The Course Creator's Vault
                </h1>
                <p className="text-xs text-gray-500">by Templimize AI</p>
              </div>
            </div>
            <div className="text-sm text-gray-600">{user?.name}</div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page Header */}
        <div className="bg-gradient-to-br from-green-600 to-teal-600 text-white rounded-2xl shadow-xl p-8 mb-8">
          <h2 className="text-4xl font-bold mb-4">🎓 Certificate Generator</h2>
          <p className="text-green-100 text-lg mb-2">
            For: <strong>{course?.title}</strong>
          </p>
          <p className="text-green-200">
            Generate beautiful completion certificates for your students
          </p>
        </div>

        {/* Certificate Form */}
        <div className="bg-white rounded-xl shadow-lg p-8 border border-gray-200">
          <div className="space-y-6">
            {/* Student Name(s) */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Student Name(s) <span className="text-red-500">*</span>
              </label>
              <textarea
                value={studentName}
                onChange={(e) => setStudentName(e.target.value)}
                rows={4}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-600 focus:border-transparent"
                placeholder="Enter student name&#10;Or enter multiple names (one per line) for bulk generation"
              />
              <p className="text-sm text-gray-500 mt-1">
                Enter one name for single certificate, or multiple names (one per line) for bulk generation
              </p>
            </div>

            {/* Instructor Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Instructor Name
              </label>
              <input
                type="text"
                value={instructorName}
                onChange={(e) => setInstructorName(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-600 focus:border-transparent"
                placeholder={user?.name || 'Your name'}
              />
            </div>

            {/* Completion Date */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Completion Date
              </label>
              <input
                type="text"
                value={completionDate}
                onChange={(e) => setCompletionDate(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-600 focus:border-transparent"
                placeholder="November 22, 2025"
              />
            </div>

            {/* Brand Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Issued By (Brand Name)
              </label>
              <input
                type="text"
                value={brandName}
                onChange={(e) => setBrandName(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-600 focus:border-transparent"
                placeholder="Your Company Name"
              />
            </div>

            {/* Preview Box */}
            <div className="bg-gray-50 border-2 border-dashed border-gray-300 rounded-xl p-8">
              <h3 className="text-lg font-bold text-gray-900 mb-4 text-center">Certificate Preview</h3>
              <div className="bg-white border-4 border-indigo-500 rounded-lg p-6 max-w-md mx-auto text-center shadow-lg">
                <div className="text-indigo-600 font-bold text-xl mb-2">CERTIFICATE</div>
                <div className="text-gray-500 text-sm mb-4">OF COMPLETION</div>
                <div className="text-gray-600 text-sm mb-2">This certifies that</div>
                <div className="text-2xl font-bold text-gray-900 mb-2 border-b-2 border-indigo-300 pb-2 inline-block px-4">
                  {studentName.split('\n')[0] || 'Student Name'}
                </div>
                <div className="text-gray-600 text-sm mt-4 mb-2">has completed</div>
                <div className="text-lg font-semibold text-indigo-600 mb-4">{course?.title}</div>
                <div className="text-gray-500 text-xs">{completionDate}</div>
              </div>
            </div>

            {/* Generate Buttons */}
            <div className="flex gap-4 pt-4">
              {studentName.includes('\n') ? (
                <button
                  onClick={handleBulkGenerate}
                  className="flex-1 bg-gradient-to-r from-green-600 to-teal-600 text-white px-6 py-4 rounded-lg font-bold hover:shadow-xl transition text-lg"
                >
                  🎓 Generate {studentName.split('\n').filter(n => n.trim()).length} Certificates
                </button>
              ) : (
                <button
                  onClick={handleGenerate}
                  className="flex-1 bg-gradient-to-r from-green-600 to-teal-600 text-white px-6 py-4 rounded-lg font-bold hover:shadow-xl transition text-lg"
                >
                  🎓 Generate Certificate
                </button>
              )}
              <button
                onClick={() => navigate(`/course/${courseId}`)}
                className="bg-gray-100 text-gray-700 px-6 py-4 rounded-lg font-semibold hover:bg-gray-200 transition"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default CertificateGenerator;
